import { createContext, ReactNode, useCallback, useMemo, useRef } from 'react';
import {
  ResponseActionHandler,
  ResponseActionScope,
  ResponseActionsContextType,
} from '../types/context';
import { debugLogger } from '../debug/DebugLogger';

const log = debugLogger.scope('responseActions');

const defaultValues: ResponseActionsContextType = {
  isProvided: false,
  registerResponseAction: () => {
    throw new Error('registerResponseAction must be implemented');
  },
  getResponseActionHandler: () => {
    throw new Error('getResponseActionHandler must be implemented');
  },
};

export const ResponseActionsContext =
  createContext<ResponseActionsContextType>(defaultValues);

const ResponseActionsProvider = ({ children }: { children: ReactNode }) => {
  const registryRef = useRef<
    Map<ResponseActionScope, Map<string, ResponseActionHandler>>
  >(new Map());

  const getScopeMap = (scope: ResponseActionScope) => {
    let scopeMap = registryRef.current.get(scope);
    if (!scopeMap) {
      scopeMap = new Map();
      registryRef.current.set(scope, scopeMap);
    }
    return scopeMap;
  };

  const registerResponseAction = useCallback(
    (
      scope: ResponseActionScope,
      name: string,
      handler: ResponseActionHandler
    ): (() => void) => {
      const scopeMap = getScopeMap(scope);

      /** @debugFeature responseActions Logs a registered response action handler: scope, name and whether it silently overwrites an existing handler of the same name. */
      log('response action handler registered', () => ({
        scope,
        name,
        overwritesExisting: scopeMap.has(name),
      }));

      scopeMap.set(name, handler);
      return () => {
        if (scopeMap.get(name) === handler) {
          scopeMap.delete(name);

          /** @debugFeature responseActions Logs an unregistered response action handler — the name no longer resolves in its scope. */
          log('response action handler unregistered', () => ({ scope, name }));
        }
      };
    },
    []
  );

  const getResponseActionHandler = useCallback(
    (
      scope: ResponseActionScope,
      name: string
    ): ResponseActionHandler | undefined => {
      return registryRef.current.get(scope)?.get(name);
    },
    []
  );

  const value = useMemo<ResponseActionsContextType>(
    () => ({
      isProvided: true,
      registerResponseAction,
      getResponseActionHandler,
    }),
    [registerResponseAction, getResponseActionHandler]
  );

  return (
    <ResponseActionsContext.Provider value={value}>
      {children}
    </ResponseActionsContext.Provider>
  );
};

export default ResponseActionsProvider;
